import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  CustomButton,
  CustomMRTTable,
  CustomModal,
} from "../../../components";
import AxiosInstance, { TokenService } from "../../../services/Api";
import styles from "../plans-pricing/PlansPricing.module.css";

const LIST_PATH = "/dashboard/masters/plan-features";

const isSuccess = (response) => response?.data?.status === true;

const getUserId = () => {
  const user = TokenService.getUser();
  return user?.id || user?.user_id || 1;
};

const PlanFeaturesList = () => {
  const navigate = useNavigate();
  const [features, setFeatures] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const fetchFeatures = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post("/saas/list/plan_feature", {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      });

      if (isSuccess(response)) {
        setFeatures(response?.data?.data || []);
        setTotalRecords(response?.data?.pagination?.total_records || 0);
      } else {
        throw new Error(response?.data?.message || "Failed to fetch plan features");
      }
    } catch (err) {
      setStatusModal({
        open: true,
        type: "error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Error fetching plan features",
      });
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await AxiosInstance.post("/saas/delete/plan_feature", {
        feature_id: deleteTarget.id,
        deleted_by: getUserId(),
      });

      if (isSuccess(response)) {
        setDeleteTarget(null);
        setStatusModal({
          open: true,
          type: "success",
          message: response?.data?.message || "Feature deleted successfully",
        });
        fetchFeatures();
      } else {
        throw new Error(response?.data?.message || "Failed to delete feature");
      }
    } catch (err) {
      setDeleteTarget(null);
      setStatusModal({
        open: true,
        type: "error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Error deleting feature",
      });
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "serial",
        header: "S.No",
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) =>
          pagination.pageIndex * pagination.pageSize + row.index + 1,
      },
      { accessorKey: "feature_name", header: "Name", size: 220 },
      { accessorKey: "feature_description", header: "Description", size: 380 },
      {
        id: "actions",
        header: "Action",
        size: 120,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <div style={{ display: "flex", gap: "8px" }}>
            <Tooltip title="Edit" arrow>
              <IconButton
                size="small"
                className={styles.iconActionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`${LIST_PATH}/create`, {
                    state: { feature: row.original },
                  });
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete" arrow>
              <IconButton
                size="small"
                className={styles.iconActionBtnDanger}
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget(row.original);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    [pagination.pageIndex, pagination.pageSize, navigate],
  );

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Plan Features</h1>
          <p className={styles.pageSubtitle}>
            Tailor your iFillip subscription with customizable feature
            modules.
          </p>
        </div>
        <div className={styles.headerActions}>
          <CustomButton
            variant="primary"
            fullWidth={false}
            className={styles.btnOrange}
            onClick={() => navigate(`${LIST_PATH}/create`)}
          >
            Create Features
          </CustomButton>
        </div>
      </div>

      <div className={styles.card}>
        <h2 className={styles.sectionTitle}>Plans</h2>
        <CustomMRTTable
          columns={columns}
          data={features}
          loading={loading}
          manualPagination
          manualFiltering={false}
          rowCount={totalRecords}
          pagination={pagination}
          onPaginationChange={setPagination}
          enablePagination
          enableSorting
          enableSearch={false}
          enableColumnFilters={false}
          enableColumnVisibility={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableHiding={false}
          enableColumnActions={false}
          enableStickyHeader
        />
      </div>

      <CustomModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete Feature"
        size="sm"
      >
        <p className={styles.confirmText}>
          Are you sure you want to delete{" "}
          <strong>{deleteTarget?.feature_name}</strong>? This action cannot
          be undone.
        </p>
        <div className={styles.confirmActions}>
          <CustomButton
            variant="outline-dark"
            fullWidth={false}
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
          >
            Cancel
          </CustomButton>
          <CustomButton
            variant="danger"
            fullWidth={false}
            onClick={confirmDelete}
            loading={deleting}
            loadingText="Deleting..."
          >
            Delete
          </CustomButton>
        </div>
      </CustomModal>

      <CustomModal
        isOpen={statusModal.open}
        onClose={() => setStatusModal((prev) => ({ ...prev, open: false }))}
        title={statusModal.type === "success" ? "Success" : "Failed"}
        size="sm"
      >
        <p className={styles.confirmText}>{statusModal.message}</p>
        <div className={styles.confirmActions}>
          <CustomButton
            variant="primary"
            fullWidth={false}
            className={styles.btnOrange}
            onClick={() =>
              setStatusModal((prev) => ({ ...prev, open: false }))
            }
          >
            OK
          </CustomButton>
        </div>
      </CustomModal>
    </div>
  );
};

export default PlanFeaturesList;
